import { Pending } from '@cluey/cluey-components';
import { useEffect, useRef, useState } from 'react';
import Frame from 'react-frame-component';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import EnrolmentLayout from '../../common/hub/EnrolmentLayout';
import { Loader } from '../../components/Loader';
import { useGetPolicy } from '../../hooks/queries/usePolicyQuery';
import { getAccountDetails } from '../../selectors/hubSelector';
import { COUNTRY, POLICY_NAME_MAP } from '../../types/hubEnums';
import { AccountDetails, PolicyType } from '../../types/hubInterfaces';
import { COUNTRY_MAP } from '../../types/hubTypes';
import { getWebsiteDomainByCountry } from '../../util/helper/website';
import { PATH_HUB_SUPPORT } from '../../util/pagePath';
import { TabNav, TabNavItem } from '../../v2/common/TabNav';

const Policy = () => {
  const { policyType } = useParams<PolicyType>();
  const { country }: AccountDetails = useSelector(getAccountDetails);
  const containerRef = useRef<HTMLDivElement>();
  const [frameHeight, setFrameHeight] = useState(0);
  const [frameMounted, setFrameMounted] = useState(Date.now());
  const [policyCountry, setPolicyCountry] = useState(country);
  const { data, isError, isLoading } = useGetPolicy({ country: policyCountry, policyType });

  const countryTabs: TabNavItem<COUNTRY_MAP['COUNTRY_CODE']>[] = [
    { value: COUNTRY.AUSTRALIA.COUNTRY_CODE, label: COUNTRY.AUSTRALIA.NAME },
    { value: COUNTRY.NEW_ZEALAND.COUNTRY_CODE, label: COUNTRY.NEW_ZEALAND.NAME },
  ];

  useEffect(() => {
    let resizeObserver: ResizeObserver | null = null;
    if (containerRef.current) {
      resizeObserver = new ResizeObserver(() => {
        const { height } = containerRef.current.getBoundingClientRect();
        setFrameHeight(height + 50);
      });
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [frameMounted]);

  const onPolicyContainerClick = (event) => {
    event.preventDefault();
    let target = event.target as HTMLAnchorElement;
    if (target.tagName === 'A') {
      let url = target.getAttribute('href');
      /* handle hrefs that are relative */
      if (!target.getAttribute('href').match(/(mailto|http)/)) {
        url = `${getWebsiteDomainByCountry(policyCountry)}${url}`;
      }
      let context = url.includes('mailto') ? '_self' : '_blank';
      window.open(url, context);
    }
  };

  return (
    <EnrolmentLayout
      sectionHeading="Support"
      sectionHeadingLink={PATH_HUB_SUPPORT}
      mainHeading={POLICY_NAME_MAP[policyType]}
      showEnrolmentStatus={false}
      showSummerModeEnrolmentStatus={false}
    >
      <div className="mb-10 px-5 md:px-10">
        <Pending
          error={isError || (data?.data?.status && data?.data?.status !== 200)}
          loading={isLoading}
          loader={<Loader />}
        >
          <TabNav
            tabs={countryTabs}
            activeTab={policyCountry}
            onTabChange={(nextCountry) => setPolicyCountry(nextCountry)}
            ariaLabel="Policy country"
            className="mb-5"
          />
          {/* 
            contentDidMount is invoked every time a different iframe content is loaded;
            the resize observer must be re-initialized thus
          */}
          <div className="container rounded-3xl bg-white px-5 py-6 shadow-md md:p-10">
            <h4 className="mb-10 font-[Merriweather] text-lg font-bold text-grey-600 md:text-2xl">Privacy policy</h4>
            <Frame
              spellCheck={false}
              width="100%"
              height={`${frameHeight}px`}
              contentDidMount={() => setFrameMounted(Date.now())}
            >
              <link rel="stylesheet" href="https://clueylearning.com.au/wp-content/themes/clueytheme/style.css" />
              {/*
              This is a separate document (react-frame-component mounts into its own
              iframe), so the app's compiled Tailwind CSS and `.link` class never reach
              it. Mirror the `.link` utility (src/tailwind.css) with literal values here
              instead, since var(--text-link)/var(--color-orange-400) aren't defined here.
            */}
              <style>{`
              a {
                font-family: 'Poppins', sans-serif;
                font-size: 1rem;
                font-weight: 400;
                font-style: normal;
                line-height: 1.5rem;
                letter-spacing: normal;
                color: #a04200 !important;
                text-decoration: underline !important;
                text-decoration-style: solid;
                text-underline-offset: auto;
                font-feature-settings: 'liga' off, 'clig' off;
                text-decoration-skip-ink: none;
                text-underline-position: from-font;
              }
              a:hover {
                color: #ff6600 !important;
              }
            `}</style>
              <div
                ref={containerRef}
                dangerouslySetInnerHTML={{ __html: data?.content?.rendered }}
                onClick={onPolicyContainerClick}
                /* Can't use tailwind here since this element is in a different browsing context */
                style={{ outline: 'none' }}
              ></div>
            </Frame>
          </div>
        </Pending>
      </div>
    </EnrolmentLayout>
  );
};

export { Policy };
